import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SignedSrcDirective } from '../../../shared/directives/signed-src.directive';
import { appointmentStatusKey } from '../../../core/utils/appointment-status.utils';
import { DoctorDashboard } from '../../../core/models/clinic-dashboard.model';
import { DashStatCard } from './stat-card';

@Component({
  selector: 'app-dash-doctor-view',
  standalone: true,
  imports: [CommonModule, TranslateModule, SignedSrcDirective, DashStatCard],
  template: `
    <div *ngIf="!data().doctor_found" class="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center text-slate-500">
      {{ 'dash_no_doctor_profile' | translate }}
    </div>

    <div *ngIf="data().doctor_found" class="space-y-6">
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <app-dash-stat-card [label]="'dash_remaining_today' | translate" icon="pi-clock" [value]="data().counts?.today_remaining" valueClass="text-primary" />
        <app-dash-stat-card [label]="'dash_done' | translate" icon="pi-check-circle" [value]="data().counts?.today_done" valueClass="text-emerald-600" />
        <app-dash-stat-card [label]="'dash_week_total' | translate" icon="pi-calendar" [value]="data().counts?.week_total" />
        <app-dash-stat-card [label]="'dash_pending_plans' | translate" icon="pi-file-edit" [value]="data().pending_plan_approvals" valueClass="text-amber-600" />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Today's agenda -->
        <div class="lg:col-span-2 bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{{ 'dash_my_agenda_today' | translate }}</p>
          <p *ngIf="!data().today_appointments?.length" class="text-sm text-slate-400 py-10 text-center">{{ 'dash_nothing_today' | translate }}</p>
          <ul class="divide-y divide-slate-100 max-h-80 overflow-y-auto pr-2 -mr-2">
            <li *ngFor="let a of data().today_appointments" class="flex items-center gap-2 sm:gap-3 py-3">
              <span class="w-11 sm:w-14 shrink-0 text-xs sm:text-sm font-black text-slate-900">{{ a.time_start }}</span>
              <img [appSignedSrc]="a.patient_photo" bucket="patient_documents" class="w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-full border border-slate-100 object-cover" alt="">
              <span class="flex-1 min-w-0 text-sm font-bold text-slate-800 truncate">{{ a.patient_name || '-' }}</span>
              <span class="text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap shrink-0"
                [ngStyle]="{ background: a.color_bg || '#f1f5f9', color: a.color_text || '#475569' }">
                {{ statusKey(a.status_name) | translate }}
              </span>
            </li>
          </ul>
        </div>

        <div class="flex flex-col gap-4">
          <!-- Next appointment -->
          <div class="flex-1 bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">{{ 'dash_next_appointment' | translate }}</p>
            <ng-container *ngIf="data().next_appointment as n; else noNext">
              <p class="text-lg font-black text-slate-900 truncate">{{ n.patient_name || '-' }}</p>
              <p class="text-sm text-slate-500 mt-1">{{ n.date | date: 'EEE d MMM' }} · {{ n.time_start }}</p>
            </ng-container>
            <ng-template #noNext><p class="text-sm text-slate-400">{{ 'dash_nothing_upcoming' | translate }}</p></ng-template>
          </div>

          <app-dash-stat-card class="flex-1" [centered]="true" [label]="'dash_no_show_30d' | translate" icon="pi-times-circle"
            [value]="data().no_show_30d?.no_show"
            [hint]="'/ ' + (data().no_show_30d?.total ?? 0)" />
        </div>
      </div>
    </div>
  `,
})
export class DashDoctorView {
  data = input.required<DoctorDashboard>();
  readonly statusKey = appointmentStatusKey;
}
